/* eslint-disable no-unused-vars */
import { useCallback, useMemo } from 'react';
import { useBranchCodeContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useBranchCode = () => {
    const [state, dispatch] = useBranchCodeContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchBranchCodes = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.BRANCH_CODES, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_BRANCH_CODES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const listBranchCodes = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.BRANCH_CODES, page, size);
                dispatch({ type: ACTIONS.SET_BRANCH_CODES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const searchBranchCodes = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchBranchCodes();
        },
        [setFilter, setPage, fetchBranchCodes]
    );

    const createBranchCode = useCallback(
        async (branchCodeData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.BRANCH_CODES, branchCodeData);
                dispatch({ type: ACTIONS.ADD_BRANCH_CODE, payload: data });

                showSuccessMessage('Branch code created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateBranchCode = useCallback(
        async (id, branchCodeData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.BRANCH_CODES}/${id}`, branchCodeData);
                dispatch({ type: ACTIONS.UPDATE_BRANCH_CODE, payload: { id, data } });

                showSuccessMessage('Branch code updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteBranchCode = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.BRANCH_CODES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_BRANCH_CODE, payload: id });

                showSuccessMessage('Branch code deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getBranchCodeById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.BRANCH_CODES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_BRANCH_CODE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    return useMemo(
        () => ({
            branchCodes: state.branchCodes,
            selectedBranchCode: state.selectedBranchCode,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            setFilter,
            setSort,
            setPage,
            fetchBranchCodes,
            listBranchCodes,
            searchBranchCodes,
            createBranchCode,
            updateBranchCode,
            deleteBranchCode,
            getBranchCodeById,
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchBranchCodes,
            listBranchCodes,
            searchBranchCodes,
            createBranchCode,
            updateBranchCode,
            deleteBranchCode,
            getBranchCodeById,
            clearError
        ]
    );
};

export default useBranchCode;
